import { useMutation } from '@tanstack/react-query'
import * as React from 'react'

import type { DemoResult, ScanResult } from './useOnboardingMachine'

import { api, ApiError } from '@/lib/api'


// ─── Mutations ──────────────────────────────────────────────────────────────

export function useCreateWebsiteMutation() {
  return useMutation({
    mutationFn: (body: { url: string; name?: string }) =>
      api.post<{ website_id: string; scan_dispatched: boolean }>('/api/onboarding/website', body),
  })
}

export function useCreateManagedWebsiteMutation() {
  return useMutation({
    mutationFn: (body: { url: string; name?: string; client_name: string }) =>
      api.post<{ website_id: string; client_id: string; scan_dispatched: boolean }>('/api/onboarding/website/managed', body),
  })
}

export function useSaveContextMutation() {
  return useMutation({
    mutationFn: (body: {
      website_id: string
      industry: string
      niche_description?: string | undefined
      name?: string | undefined
      accepted_suggestion: boolean
    }) => api.post<{ saved: true }>('/api/onboarding/business-context', body),
  })
}

export function useSuggestPromptsMutation() {
  return useMutation({
    mutationFn: (body: { website_id?: string | undefined; niche_description?: string | undefined }) =>
      api.post<{ suggestions?: string[]; used_ai?: boolean }>('/api/suggest-prompts', body),
  })
}

export function useCompleteOnboardingMutation() {
  return useMutation({
    mutationFn: () => api.post('/api/onboarding/complete', {}),
  })
}

export function useRunSimulationMutation() {
  return useMutation({
    mutationFn: (body: { queries: { query_text: string }[]; platforms: string[]; website_id?: string | undefined }) =>
      api.post<{ failures: { platform: string; error: string }[]; result_details?: DemoResult[] }>('/api/simulate', body),
  })
}

// websiteId is deliberately camelCase here, unlike this file's other hooks
// (which mostly send website_id) — it matches POST /api/competitors' actual
// request schema. Do not "fix" this to snake_case to match its neighbours.
export function useAddCompetitorMutation() {
  return useMutation({
    mutationFn: (body: { websiteId: string; name: string; url: string }) =>
      api.post('/api/competitors', body),
  })
}

export { ApiError }

// ─── Scan polling ───────────────────────────────────────────────────────────

const POLL_INTERVAL_MS = 2500
const POLL_CEILING_MS = 45_000

export function useScanPolling(
  websiteId: string | null,
  active: boolean,
  onUpdate: (scan: ScanResult) => void,
  onTimeout: () => void
) {
  const onUpdateRef = React.useRef(onUpdate)
  const onTimeoutRef = React.useRef(onTimeout)
  onUpdateRef.current = onUpdate
  onTimeoutRef.current = onTimeout

  React.useEffect(() => {
    if (!active || !websiteId) return

    let cancelled = false
    const start = Date.now()

    async function poll() {
      if (cancelled) return
      if (Date.now() - start > POLL_CEILING_MS) {
        onTimeoutRef.current()
        return
      }
      try {
        const scan = await api.get<ScanResult>(`/api/onboarding/scan?website_id=${websiteId}`)
        if (cancelled) return
        onUpdateRef.current(scan)
        if (scan.status === 'running') {
          setTimeout(poll, POLL_INTERVAL_MS)
        }
      } catch {
        if (!cancelled) onTimeoutRef.current()
      }
    }

    void poll()
    return () => { cancelled = true }
  }, [websiteId, active])
}
